<template>
    <v-row class="mt-4">
        <!-- Pickup Location -->
        <v-col cols="12" sm="6" md="4">
            <v-autocomplete v-model="form.sourceAddress" :items="pickupSuggestions" :loading="pickupLoading"
                :list-props="{ class: 'custom-list', maxWidth: '385px' }" label="From"
                placeholder="Enter Pickup Location" variant="outlined" hide-no-data no-filter item-title="description"
                item-value="place_id" @update:search="fetchPickupSuggestions" @update:modelValue="onPickupSelected"
                return-object>
                <template #prepend-inner>
                    <v-icon style="color: #005886">mdi-map-marker</v-icon>
                </template>
            </v-autocomplete>
        </v-col>
        <v-col cols="12" sm="6" md="4">
            <v-autocomplete v-model="form.destinationAddress" :items="dropSuggestions" :loading="pickupLoading"
                :list-props="{ class: 'custom-list', maxWidth: '385px' }" label="To" placeholder="Enter Drop Location"
                variant="outlined" hide-no-data no-filter item-title="description" item-value="place_id"
                @update:search="fetchDropSuggestions" @update:modelValue="onDropSelected" clearable return-object>
                <template #prepend-inner>
                    <v-icon style="color: #005886">mdi-map-marker</v-icon>
                </template>
            </v-autocomplete>
        </v-col>

        <!-- Pickup Date -->
        <v-col cols="12" sm="6" md="2">
            <v-menu v-model="menuDateRental" :close-on-content-click="false" transition="scale-transition" offset-y
                min-width="auto">
                <template #activator="{ props }">
                    <v-text-field v-model="formattedRentalDate" label="Pickup Date" readonly v-bind="props"
                        variant="outlined" color="#1976D2" required>
                        <template #prepend-inner>
                            <v-icon style="color: #005886">mdi-calendar</v-icon>
                        </template>
                    </v-text-field>
                </template>
                <v-date-picker v-model="datePickerValue" :min="getTodayLocal()" @update:modelValue="onDateSelected"
                    hide-actions />
            </v-menu>
        </v-col>

        <!-- Pickup Time -->
        <v-col cols="12" sm="6" md="2">
            <v-text-field v-model="form.startTime" label="Pickup Time" type="time" variant="outlined" required
                color="#1976D2">
                <template #prepend-inner>
                    <v-icon style="color: #005886">mdi-clock</v-icon>
                </template>
            </v-text-field>
        </v-col>

        <!-- Duration & KM Selector -->
        <v-col cols="12">
            <div class="duration-selector">
                <label :class="$display.smAndDown == true? 'text-body-2' : ''">Select length of time for which you require a cab</label>
                <div class="duration-input d-flex flex-wrap align-center justify-between">
                    <v-btn icon @click="decreaseDuration" :disabled="form.duration <= 1">
                        <v-icon>mdi-minus</v-icon>
                    </v-btn>
                    <div class="duration-display text-center">
                        <div class="font-weight-bold" :class="$display.smAndDown == true ? 'text-subtitle-1' : 'text-h6'">
                            {{ form.duration }} Hours
                        </div>
                        <div>{{ form.km }} km included</div>
                    </div>
                    <v-btn icon @click="increaseDuration" :disabled="form.duration >= 12">
                        <v-icon>mdi-plus</v-icon>
                    </v-btn>
                </div>
            </div>
        </v-col>
    </v-row>
</template>

<script>
export default {
    name: "RentalForm",
    props: {
        modelValue: {
            type: Object,
            required: true,
        },
        pickupSuggestions: Array,
        dropSuggestions: Array,
        pickupLoading: Boolean,
    },
    emits: ["update:modelValue", "fetchPickupSuggestions", "fetchDropSuggestions", "pickupSelected", "dropSelected"],
    data() {
        return {
            menuDateRental: false,
            formattedRentalDate: "", // Display-only date
            datePickerValue: null, // For v-date-picker binding
            durationMap: {
                1: 5,
                2: 6,
                3: 7,
                4: 9,
                5: 8,
                6: 12,
                7: 13,
                8: 10,
                9: 14,
                10: 15,
                11: 16,
                12: 11
            }
        };
    },
    computed: {
        form: {
            get() {
                return this.modelValue;
            },
            set(val) {
                this.$emit("update:modelValue", val);
            },
        },
    },
    mounted() {
        // Initialize formattedRentalDate if startDate exists
        if (this.form.startDate) {
            this.formattedRentalDate = this.form.startDate;
            this.datePickerValue = new Date(this.form.startDate);
        }
    },
    watch: {
        'form.duration'(val) {
            if (this.durationMap[val]) {
                this.form.tripType = this.durationMap[val]
            } else {
                this.form.tripType = null // fallback if duration not in map
            }
        }
    },
    methods: {
        formatDateLocal(date) {
            if (!date) return "";
            return (
                date.getFullYear() +
                "-" +
                String(date.getMonth() + 1).padStart(2, "0") +
                "-" +
                String(date.getDate()).padStart(2, "0")
            );
        },
        getTodayLocal() {
            const today = new Date();
            return this.formatDateLocal(today);
        },
        onDateSelected(val) {
            this.menuDateRental = false;
            if (val) {
                // val is a Date object from v-date-picker
                const formattedDate = this.formatDateLocal(val);
                this.form.startDate = formattedDate;
                this.formattedRentalDate = formattedDate;
                this.datePickerValue = val;
                this.$emit("update:modelValue", { ...this.form });
            }
        },
        decreaseDuration() {
            if (this.form.duration > 1) {
                this.form.duration -= 1;
                this.form.km = this.form.duration * 10;
                this.$emit("update:modelValue", { ...this.form });
            }
        },
        increaseDuration() {
            if (this.form.duration < 12) {
                this.form.duration += 1;
                this.form.km = this.form.duration * 10;
                this.$emit("update:modelValue", { ...this.form });
            }
        },
        fetchPickupSuggestions(query) {
            this.$emit("fetchPickupSuggestions", query);
        },
        fetchDropSuggestions(query) {
            this.$emit("fetchDropSuggestions", query);
        },
        onPickupSelected(val) {
            this.$emit("pickupSelected", val);
        },
        onDropSelected(val) {
            this.$emit("dropSelected", val);
        },
    },
};
</script>
